/**
 * Line switcher and line picker (W2-07; DESIGN.md §5.5 "Active line": `L` fuzzy line switcher by name or `#index`;
 * "Parent": a picker that greys out the lines that would loop). One dialog, three uses:
 *  - activate: Enter makes the line active and selects it (the panel scrolls to it);
 *  - parent: choose the new parent of the given lines (or "No parent"); lines that would make one of them its own
 *    ancestor are listed but disabled ("would loop"); then the parent prompt asks keep numbers / keep place;
 *  - alsoShow: add (or remove) a line whose notes the lane shows as ghosts.
 * Keyboard: type to filter; Up / Down / PageUp / PageDown move; Enter chooses; Esc closes.
 */

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { useDocValue, useEditorValue } from './hooks';
import { activateLine, setParentFlow, toggleAlsoShow } from './lineActions';
import { m } from './lines.messages';
import {
  buildLineTable,
  indexQuery,
  isAncestorOrSelf,
  matchLines,
  TYPE_ICONS,
  UI_ELEMENT_ICONS,
  type LineInfo,
} from './lineModel';
import type { PickerMode } from './linesUi';
import s from './Lines.module.css';

/** Most lines listed at once (type more to narrow down). */
export const SWITCHER_LIMIT = 200;

const TOPICS = ['lines', 'lineOrder', 'tracks', 'keys', 'notes', 'groups', 'assets'] as const;

interface PickerOption {
  readonly key: string;
  readonly line: LineId | null;
  readonly info: LineInfo | null;
  readonly disabled: string | null;
  readonly note: string | null;
}

export interface LineSwitcherProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly mode: PickerMode;
  readonly onClose: () => void;
}

export function LineSwitcher(props: LineSwitcherProps) {
  const { services, store, mode } = props;
  const t = useT();
  const baseId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const table = useDocValue(store, buildLineTable, TOPICS);
  const activeLine = useEditorValue(services, (st) => st.activeLine);
  const alsoShow = useEditorValue(services, (st) => st.alsoShow);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<{ readonly query: string; readonly index: number } | null>(null);

  const options = useMemo((): PickerOption[] => {
    const out: PickerOption[] = [];
    const moving = mode.kind === 'parent' ? mode.lines.filter((id) => table.byId.has(id)) : [];
    const currentParents = new Set(moving.map((id) => table.byId.get(id)?.parent ?? null));
    if (mode.kind === 'parent') {
      out.push({
        key: 'none',
        line: null,
        info: null,
        disabled: null,
        note: currentParents.size === 1 && currentParents.has(null) ? t(m.pickerCurrent) : null,
      });
    }
    for (const { info } of matchLines(table, query, SWITCHER_LIMIT)) {
      let disabled: string | null = null;
      let note: string | null = null;
      if (mode.kind === 'parent') {
        if (moving.some((id) => isAncestorOrSelf(store.doc, id, info.id))) disabled = t(m.pickerWouldLoop);
        else if (currentParents.size === 1 && currentParents.has(info.id)) note = t(m.pickerCurrent);
      } else if (mode.kind === 'alsoShow') {
        if (info.id === activeLine) disabled = t(m.pickerActive);
        else if (alsoShow.includes(info.id)) note = t(m.pickerShown);
      } else if (info.id === activeLine) {
        note = t(m.pickerActive);
      }
      out.push({ key: String(info.id), line: info.id, info, disabled, note });
    }
    return out;
  }, [table, query, mode, store, activeLine, alsoShow, t]);

  const firstEnabled = Math.max(
    0,
    options.findIndex((o) => o.disabled === null),
  );
  const index =
    cursor !== null && cursor.query === query && cursor.index < options.length ? cursor.index : firstEnabled;
  const optionId = (i: number): string => `${baseId}-opt-${String(i)}`;

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${String(index)}"]`) ?? null;
    if (el === null) return;
    try {
      el.scrollIntoView({ block: 'nearest' });
    } catch {
      // No layout (tests): nothing to scroll.
    }
  }, [index]);

  const choose = (o: PickerOption | undefined): void => {
    if (o === undefined || o.disabled !== null) return;
    switch (mode.kind) {
      case 'activate':
        if (o.line !== null) activateLine(services, o.line, { select: true, reveal: true });
        props.onClose();
        return;
      case 'parent':
        props.onClose();
        void setParentFlow(services, mode.lines, o.line);
        return;
      case 'alsoShow':
        if (o.line !== null) toggleAlsoShow(services, [o.line]);
        props.onClose();
        return;
    }
  };

  const move = (to: number): void => {
    if (options.length === 0) return;
    setCursor({ query, index: Math.max(0, Math.min(options.length - 1, to)) });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'ArrowDown':
        move(index + 1);
        break;
      case 'ArrowUp':
        move(index - 1);
        break;
      case 'PageDown':
        move(index + 8);
        break;
      case 'PageUp':
        move(index - 8);
        break;
      case 'Enter':
        if (e.nativeEvent.isComposing) return;
        choose(options[index]);
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const asked = indexQuery(query);
  const missingIndex = asked !== null && !table.order.some((_, i) => i === asked) ? asked : null;
  const lineCount = options.filter((o) => o.line !== null).length;
  const title =
    mode.kind === 'activate'
      ? t(m.switcherTitle)
      : mode.kind === 'parent'
        ? t(m.pickParentTitle, { count: mode.lines.length })
        : t(m.pickAlsoShowTitle);

  return (
    <Dialog open={true} size="sm" title={title} onClose={props.onClose}>
      <input
        className={s.pickerInput}
        role="combobox"
        aria-expanded={true}
        aria-controls={`${baseId}-list`}
        aria-activedescendant={options.length > 0 ? optionId(index) : undefined}
        aria-autocomplete="list"
        aria-label={t(m.switcherInput)}
        placeholder={t(m.switcherPlaceholder)}
        value={query}
        autoFocus
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
        }}
        onKeyDown={onKeyDown}
      />
      <ul ref={listRef} id={`${baseId}-list`} role="listbox" aria-label={title} className={s.pickerList}>
        {options.map((o, i) => {
          const info = o.info;
          const icon =
            info === null
              ? 'CircleSlash'
              : info.uiElement !== null
                ? UI_ELEMENT_ICONS[info.uiElement]
                : TYPE_ICONS[info.type];
          const detail = [
            info === null ? null : t(m.typeName, { type: info.type }),
            info === null ? null : t(m.notesCount, { count: info.notes }),
            info?.groupName ?? null,
            o.note,
            o.disabled,
          ]
            .filter((x): x is string => x !== null && x !== '')
            .join(' · ');
          return (
            <li
              key={o.key}
              id={optionId(i)}
              data-index={i}
              role="option"
              aria-selected={i === index}
              aria-disabled={o.disabled !== null ? true : undefined}
              className={s.option}
              onMouseMove={() => {
                if (i !== index) setCursor({ query, index: i });
              }}
              onClick={() => {
                choose(o);
              }}
            >
              <span className={s.index}>{info === null ? '' : `#${String(info.index)}`}</span>
              <Icon name={icon} size={14} />
              <span className={s.name}>
                {info === null ? t(m.pickerNoParent) : info.name.trim() === '' ? t(m.unnamed) : info.name}
              </span>
              <span className={s.optionDetail}>{detail}</span>
            </li>
          );
        })}
      </ul>
      <p className={s.pickerStatus} role="status">
        {missingIndex !== null
          ? t(m.switcherNoIndex, { index: missingIndex, count: table.order.length })
          : t(m.switcherCount, { count: lineCount })}
      </p>
    </Dialog>
  );
}
